/**
 * Tempo de resposta (SLA) e Modo Ponte.
 *
 * A espera começa na mensagem da cliente e só termina quando alguém da
 * equipe responde — a IA conversando no Modo Ponte não zera o contador.
 */
import { z } from "zod";

export const NIVEIS_ESPERA = ["normal", "atencao", "urgente", "estourado"] as const;
export type NivelEspera = (typeof NIVEIS_ESPERA)[number];

export const ROTULO_NIVEL_ESPERA: Record<NivelEspera, string> = {
  normal: "Normal",
  atencao: "Atenção",
  urgente: "Urgente",
  estourado: "Prazo estourado",
};

const minutos = z
  .number()
  .int()
  .min(1)
  .max(24 * 60);

export const configSlaSchema = z
  .object({
    /** A partir de quantos minutos esperando cada nível começa. */
    atencaoMin: minutos.default(5),
    urgenteMin: minutos.default(15),
    estouradoMin: minutos.default(30),
    /** Avisa a vendedora (uma vez) quando uma conversa dela fica urgente. */
    avisarVendedora: z.boolean().default(true),
    /** Avisa a gerência quando o prazo estoura. */
    avisarGestao: z.boolean().default(true),
  })
  .refine((c) => c.atencaoMin < c.urgenteMin && c.urgenteMin < c.estouradoMin, {
    message: "Os tempos precisam crescer: atenção < urgente < estourado.",
  });
export type ConfigSla = z.infer<typeof configSlaSchema>;
export const CONFIG_SLA_PADRAO: ConfigSla = configSlaSchema.parse({});

export function nivelEspera(
  desdeIso: string,
  limites: ConfigSla = CONFIG_SLA_PADRAO,
  agora = Date.now(),
): NivelEspera {
  const min = (agora - new Date(desdeIso).getTime()) / 60_000;
  if (min >= limites.estouradoMin) return "estourado";
  if (min >= limites.urgenteMin) return "urgente";
  if (min >= limites.atencaoMin) return "atencao";
  return "normal";
}

export const configPonteSchema = z.object({
  /** A IA conversa com a cliente enquanto nenhuma vendedora responde. */
  ativo: z.boolean().default(false),
  /** Minutos de espera sem resposta da equipe até a IA entrar. */
  aposMin: z.number().int().min(1).max(120).default(3),
  /** Respostas da IA por espera; depois disso ela para e a gerência é avisada. */
  maxRespostas: z.number().int().min(1).max(30).default(8),
});
export type ConfigPonte = z.infer<typeof configPonteSchema>;
export const CONFIG_PONTE_PADRAO: ConfigPonte = configPonteSchema.parse({});

export const configAtendimentoSchema = z.object({ sla: configSlaSchema, ponte: configPonteSchema });
export type ConfigAtendimento = z.infer<typeof configAtendimentoSchema>;

/** O que a IA entendeu enquanto conduziu (vai para a vendedora). */
export interface ResumoPonte {
  resumo: string;
  procura: string | null;
  ocasiao: string | null;
  tamanho: string | null;
  cores: string | null;
  prazo: string | null;
  orcamento: string | null;
  produtosSugeridos: string[];
  respostas: number;
  atualizadoEm: string;
}
